import React, { useCallback, useEffect, useState } from "react";
import { X, GraduationCap, Plus, ChevronLeft, Trash2, Loader2, BookOpen } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { LessonForm } from "./LessonForm";
import { LessonPlayer } from "./LessonPlayer";
import { LessonQuiz, LessonNotes, LessonAsk } from "./LessonExtras";

const LANG = { en: "English", hi: "हिंदी", mr: "मराठी" };
const TABS = [["lesson", "Lesson"], ["quiz", "Quiz"], ["notes", "Revision notes"], ["ask", "Follow-ups"]];

export function LearnDrawer({ open, onClose, balance, onBalance }) {
  const [view, setView] = useState("list");
  const [lessons, setLessons] = useState([]);
  const [options, setOptions] = useState(null);
  const [active, setActive] = useState(null);
  const [tab, setTab] = useState("lesson");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/learn/lessons"); setLessons(data.lessons); } catch {}
  }, []);
  useEffect(() => {
    if (!open) return;
    load(); setView("list"); setActive(null);
    api.get("/learn/options").then(({ data }) => setOptions(data)).catch(() => {});
  }, [open, load]);

  const openLesson = async (id) => {
    try {
      const { data } = await api.get(`/learn/lessons/${id}`);
      setActive(data.lesson); setTab("lesson"); setView("lesson");
    } catch { toast.error("Couldn't open that lesson."); }
  };

  const pendingId = active?.status === "generating" ? active.id : null;
  useEffect(() => {
    if (!pendingId) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/learn/lessons/${pendingId}`);
        if (data.lesson.status !== "generating") {
          clearInterval(t); setActive(data.lesson); load();
          if (data.lesson.status === "ready") { toast.success("Your lesson is ready."); onBalance?.(); }
          else toast.error("Lesson generation failed — you weren't charged.");
        }
      } catch {}
    }, 3000);
    return () => clearInterval(t);
  }, [pendingId, load, onBalance]);

  const create = async (form) => {
    setBusy(true);
    try {
      const { data } = await api.post("/learn/lessons", form);
      setActive(data.lesson); setView("lesson"); setTab("lesson"); load();
    } catch (e) { toast.error(e?.response?.status === 402 ? "Not enough credits." : e?.response?.data?.detail || "Couldn't start the lesson."); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this lesson? This can't be undone.")) return;
    try { await api.delete(`/learn/lessons/${id}`); toast.success("Lesson deleted."); load(); if (active?.id === id) { setActive(null); setView("list"); } }
    catch { toast.error("Couldn't delete the lesson."); }
  };

  const listPending = view === "list" && lessons.some((l) => l.status === "generating");
  useEffect(() => {
    if (!listPending) return;
    const t = setInterval(() => { load(); onBalance?.(); }, 4000);
    return () => clearInterval(t);
  }, [listPending, load, onBalance]);

  if (!open) return null;
  const accent = active?.presenter === "sam" ? "#D6D6D6" : "#E4E4E4";
  const ready = active?.status === "ready";

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <aside data-testid="learn-drawer" className="fixed z-50 top-0 right-0 h-full w-full sm:w-[720px] glass-panel border-l border-white/10 flex flex-col fade-in-up">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2">
            {view !== "list" && <button data-testid="learn-back" onClick={() => { setView("list"); setActive(null); }} className="text-slate-400 hover:text-white"><ChevronLeft className="w-5 h-5" /></button>}
            <div>
              <div className="label-mono">Stealth Learn</div>
              <h2 className="font-heading text-lg font-normal tracking-wide mt-0.5 flex items-center gap-2"><GraduationCap className="w-4 h-4 text-mono-300" /> {view === "new" ? "New lesson" : active?.title || "My lessons"}</h2>
            </div>
          </div>
          <button data-testid="learn-close" onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto thin-scroll p-5">
          {view === "list" && (
            <div className="space-y-3">
              <button data-testid="learn-new" onClick={() => setView("new")} className="w-full rounded-xl border border-dashed border-mono-400/40 py-4 text-sm text-mono-200 flex items-center justify-center gap-2 hover:bg-mono-400/5"><Plus className="w-4 h-4" /> New lesson</button>
              {lessons.length === 0 && <div className="text-sm text-slate-500 text-center py-8 reading">Snap a textbook page or type any topic — Anna or Sam will teach it with animated diagrams.</div>}
              {lessons.map((l) => (
                <div key={l.id} data-testid="lesson-item" onClick={() => openLesson(l.id)} className="group rounded-xl border border-white/10 px-4 py-3 cursor-pointer hover:border-mono-400/40 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="text-sm flex items-center gap-2"><BookOpen className="w-3.5 h-3.5 text-mono-300" /> <span className="truncate">{l.title}</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{l.subject} · {l.level} · {LANG[l.language]} · {l.presenter === "sam" ? "Sam" : "Anna"} · {l.status === "ready" ? `${l.cost} cr` : l.status}{l.best_score != null ? ` · best ${l.best_score}` : ""}</div>
                  </div>
                  <button data-testid="lesson-delete" onClick={(e) => { e.stopPropagation(); del(l.id); }} className="text-slate-500 hover:text-red-400 opacity-60 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}
          {view === "new" && <LessonForm options={options} balance={balance} busy={busy} onSubmit={create} />}
          {view === "lesson" && active && !ready && (
            <div className="flex flex-col items-center justify-center py-20 gap-3 text-center" data-testid="lesson-status">
              {active.status === "generating"
                ? <><Loader2 className="w-8 h-8 animate-spin text-mono-300" /><div className="reading text-slate-300">{active.presenter === "sam" ? "Sam" : "Anna"} is preparing your lesson…</div><div className="text-xs text-slate-500">Diagrams, narration, quiz and notes — usually under a minute.</div></>
                : <div className="reading text-red-300">Lesson generation failed — you weren't charged.</div>}
            </div>
          )}
          {view === "lesson" && ready && (
            <div className="space-y-5">
              <div className="flex gap-1 border-b border-white/10" data-testid="lesson-tabs">
                {TABS.map(([k, n]) => (
                  <button key={k} data-testid={`lesson-tab-${k}`} onClick={() => setTab(k)} className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${tab === k ? "border-mono-300 text-mono-200" : "border-transparent text-slate-400 hover:text-white"}`}>{n}</button>
                ))}
              </div>
              {tab === "lesson" && <LessonPlayer lesson={active} accent={accent} onFinished={() => setTab("quiz")} />}
              {tab === "quiz" && <LessonQuiz lesson={active} />}
              {tab === "notes" && <LessonNotes lesson={active} />}
              {tab === "ask" && <LessonAsk lesson={active} cost={options?.pricing?.learn_followup ?? 1} onAnswered={() => onBalance?.()} />}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
